import { useMemo, useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatMoney, formatScopedMoneyAuto } from "@/lib/money";
import { usePagination } from "@/lib/usePagination";
import { Pager } from "@/components/ui/Pager";

/**
 * DataTable — the canonical Mixpanel data table for retention-iq, matching the
 * ads-iq Campaigns/Performance table look: a soft rounded-2xl card wrapper,
 * uppercase muted column headers, hairline row dividers, hover row tint, and
 * right-aligned mono money cells. Sorting and an optional rank column are
 * built in. Pages declare `columns` + `rows`; they never re-roll table markup.
 *
 * Usage (`data` is the API response: its rows plus its `currency`):
 *   <DataTable
 *     rows={data.campaigns}
 *     rowKey={(c) => c.id}
 *     rank
 *     defaultSort={{ key: "revenue", dir: "desc" }}
 *     columns={[
 *       { key: "name", header: "Campaign", cell: (c) => c.name },
 *       { key: "status", header: "Status", cell: (c) => <StatusPill status={c.status} /> },
 *       { key: "revenue", header: "Revenue", align: "right",
 *         sortValue: (c) => c.revenue, cell: (c) => <MoneyCell value={c.revenue} currency={data.currency} /> },
 *     ]}
 *   />
 */

export type SortDir = "asc" | "desc";

export interface DataColumn<T> {
  /** Unique id; also the sort key. */
  key: string;
  /** Column header label. */
  header: ReactNode;
  /** Optional quiet ⓘ beside the header (e.g. a MoneyScopeNote), kept outside the sort button. */
  headerInfo?: ReactNode;
  /** Cell renderer. */
  cell: (row: T, index: number) => ReactNode;
  /** Provide to make the column sortable (returns the comparable value). */
  sortValue?: (row: T) => number | string | null | undefined;
  align?: "left" | "right" | "center";
  /** Extra classes on the <td>. */
  className?: string;
  /** Extra classes on the <th>. */
  headerClassName?: string;
}

export interface DataTableProps<T> {
  columns: DataColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  /** Show a 1-based rank column reflecting the sorted display order. */
  rank?: boolean;
  defaultSort?: { key: string; dir: SortDir };
  loading?: boolean;
  loadingText?: string;
  emptyText?: string;
  onRowClick?: (row: T) => void;
  /** Sticky header — needs a bounded height to have something to stick to. */
  stickyHeader?: boolean;
  /** e.g. "calc(100vh - 260px)". Applies an inner scroll container. */
  maxHeight?: string;
  /** Extra classes on the outer card. */
  className?: string;
  /**
   * Opt-in "top N then paginate" — when set, the (sorted) rows are sliced to
   * `pageSize` and a self-hiding <Pager> renders below. Omit to render every
   * row (today's default). Do NOT set this if the CALLER already wraps the
   * table in usePagination()+<Pager> and passes a pre-sliced `rows` — that
   * would double-paginate. House default when opting in is 10.
   */
  pageSize?: number;
  /** Noun for the pager count label when `pageSize` is set. Defaults to "rows". */
  pageNoun?: string;
}

const alignClass = (a?: "left" | "right" | "center") =>
  a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  rank = false,
  defaultSort,
  loading = false,
  loadingText = "Loading…",
  emptyText = "No rows match these filters.",
  onRowClick,
  stickyHeader = false,
  maxHeight,
  className,
  pageSize,
  pageNoun = "rows",
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(defaultSort?.key ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(defaultSort?.dir ?? "desc");

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortValue) return rows;
    const mult = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = col.sortValue!(a);
      const bv = col.sortValue!(b);
      // Nullish sorts to the bottom regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * mult;
      return String(av).localeCompare(String(bv)) * mult;
    });
  }, [rows, columns, sortKey, sortDir]);

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const colCount = columns.length + (rank ? 1 : 0);

  // Opt-in "top N + paginate". When `pageSize` is unset we build a single page
  // holding every row, so pager.pageCount === 1 and <Pager> renders nothing —
  // i.e. zero behaviour change for callers that don't opt in (and no
  // double-paging for callers that pre-slice their own rows).
  const pager = usePagination(sorted, pageSize ?? Math.max(sorted.length, 1));
  const display = pager.pageItems;

  return (
    <div className={cn("rounded-2xl bg-bg-card shadow-mx overflow-hidden", className)}>
      {/* Horizontal scroll when the table is wider than its container (e.g. a
          narrow grid cell) — scroll instead of clipping the rightmost columns.
          Vertical scroll only when maxHeight is set. */}
      <div className={cn("overflow-x-auto", maxHeight && "overflow-y-auto")} style={maxHeight ? { maxHeight } : undefined}>
        <table className="min-w-full text-sm">
          <thead
            className={cn(
              "border-b border-border-lighter",
              stickyHeader && "sticky top-0 bg-bg-card z-10",
            )}
          >
            <tr>
              {rank && (
                <th className="px-5 py-3.5 w-10 text-left text-[11px] uppercase tracking-[0.08em] font-semibold text-text-muted">
                  #
                </th>
              )}
              {columns.map((col) => {
                const sortable = !!col.sortValue;
                const active = sortKey === col.key;
                return (
                  <th
                    key={col.key}
                    className={cn(
                      "px-5 py-3.5 text-[11px] uppercase tracking-[0.08em] font-semibold text-text-muted",
                      alignClass(col.align),
                      col.headerClassName,
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.key)}
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-text-primary transition-colors",
                          col.align === "right" && "flex-row-reverse",
                          active && "text-text-primary",
                        )}
                      >
                        {col.header}
                        {active &&
                          (sortDir === "asc" ? (
                            <ChevronUp size={12} />
                          ) : (
                            <ChevronDown size={12} />
                          ))}
                      </button>
                    ) : (
                      col.header
                    )}
                    {col.headerInfo && <span className="ml-1 inline-flex align-middle">{col.headerInfo}</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} className="px-5 py-10 text-center text-text-muted">
                  {loadingText}
                </td>
              </tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="px-5 py-10 text-center text-text-muted">
                  {emptyText}
                </td>
              </tr>
            ) : (
              display.map((row, i) => (
                <tr
                  key={rowKey(row, i)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    "border-b border-border-lighter last:border-0 transition-colors",
                    onRowClick ? "cursor-pointer hover:bg-bg-hover" : "hover:bg-bg-hover",
                  )}
                >
                  {rank && (
                    <td className="px-5 py-4 text-text-muted tabular-nums">
                      {pager.from + i}
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn("px-5 py-4", alignClass(col.align), col.className)}
                    >
                      {col.cell(row, i)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pager.pageCount > 1 && (
        <div className="px-5 pb-3.5">
          <Pager p={pager} noun={pageNoun} />
        </div>
      )}
    </div>
  );
}

/**
 * Right-aligned mono money cell. Pass a pre-formatted string, or a number with
 * the response's `currency`, always passed (null → bare number; null value → "—").
 * `compact` shows amounts of 1,000 or more as "€12K" / "€1.2M".
 */
export function MoneyCell({
  value,
  currency,
  decimals,
  compact,
  className,
}: {
  value: number | string | null | undefined;
  /** The response's currency; null renders a bare number (never an assumed symbol). */
  currency: string | null | undefined;
  decimals?: number;
  compact?: boolean;
  className?: string;
}) {
  const text =
    typeof value === "string"
      ? value
      : compact
        ? formatScopedMoneyAuto(value, { currency }, { decimals })
        : formatMoney(value, currency, { decimals });
  return (
    <span className={cn("font-mono tabular-nums text-text-primary", className)}>{text}</span>
  );
}

/**
 * StatusPill — normalises the common status vocab (active/paused/removed/draft)
 * to a tone + label. Same superset ads-iq uses, so Campaigns/Flows status cells
 * render identically.
 */
export function StatusPill({ status }: { status: string | null | undefined }) {
  const norm = (status ?? "").trim().toUpperCase();
  const ACTIVE = new Set(["ENABLED", "ACTIVE", "ENABLE", "LIVE", "PUBLISHED"]);
  // "sent" is a terminal/delivered state, NOT "active" — a sent campaign must
  // read "Sent", not "Active". Kept out of the ACTIVE bucket on purpose.
  const SENT = new Set(["SENT"]);
  const PAUSED = new Set(["PAUSED", "DISABLED", "DISABLE", "INACTIVE", "MANUAL"]);
  const DRAFT = new Set(["DRAFT", "SCHEDULED", "QUEUED", "PENDING"]);
  const REMOVED = new Set(["REMOVED", "ARCHIVED", "DELETED", "CANCELLED", "CANCELED"]);

  let label: string;
  let tone: string;
  if (ACTIVE.has(norm)) {
    label = "Active";
    tone = "bg-status-published-bg text-status-published";
  } else if (SENT.has(norm)) {
    label = "Sent";
    tone = "bg-status-published-bg text-status-published";
  } else if (PAUSED.has(norm)) {
    label = "Paused";
    tone = "bg-bg-subtle text-text-secondary";
  } else if (DRAFT.has(norm)) {
    label = norm.charAt(0) + norm.slice(1).toLowerCase();
    tone = "bg-status-declining-bg text-status-declining";
  } else if (REMOVED.has(norm)) {
    label = "Removed";
    tone = "bg-bg-subtle text-text-muted";
  } else {
    label = status || "—";
    tone = "bg-bg-subtle text-text-muted";
  }
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
        tone,
      )}
    >
      {label}
    </span>
  );
}
